import {
  ActiveShoppingCartQuery,
  useActiveShoppingCartQuery,
  useRemoveCartItemMutation,
} from '@/generated/graphql'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useEffect, useMemo, useRef } from 'react'
import { useCookie } from '@/Cookies/useCookies'
import { usePathname } from 'next/navigation'
import { BRAND_COOKIE, COUNTRY_COOKIE } from '@/lib/auth/marketCookies'
import {
  UK_STOREFRONT_CURRENCY_ID,
  US_STOREFRONT_CURRENCY_ID,
} from '@/lib/checkout/regionalStoreDefaults'
import { getBrandCodeForHost } from '@/lib/seo/marketUrls'

type UseShoppingCartOptions = {
  conventionCartToken?: string | null
  includeConventionCart?: boolean
  enabled?: boolean
}

const GUEST_CART_TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const INVALID_CART_IDENTIFIER_MESSAGE = 'No valid customer ID provided'
const CART_QUERY_STALE_TIME_MS = 30_000
const CART_QUERY_GC_TIME_MS = 5 * 60_000

const normalizeCartToken = (token: unknown): string | null => {
  if (token == null) return null

  if (typeof token === 'number') {
    if (!Number.isFinite(token) || token < 0 || !Number.isInteger(token))
      return null
    const asString = String(token).trim()
    return asString.length > 0 ? asString : null
  }

  if (typeof token === 'string') {
    const normalizedToken = token.trim()
    if (normalizedToken.length === 0 || normalizedToken.length > 100)
      return null
    // Basic validation for reasonable token format
    if (!/^[a-zA-Z0-9_-]+$/.test(normalizedToken)) return null
    return normalizedToken
  }

  return null
}

const isGuestCartToken = (token: unknown): boolean => {
  const normalizedToken = normalizeCartToken(token)
  return (
    normalizedToken !== null && GUEST_CART_TOKEN_PATTERN.test(normalizedToken)
  )
}

export const useShoppingCart = (options: UseShoppingCartOptions = {}) => {
  const {
    conventionCartToken = null,
    includeConventionCart = false,
    enabled = true,
  } = options
  const pathname = usePathname()
  const routeConventionCartToken =
    pathname?.startsWith('/checkout/pay') === true &&
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('conventionCartToken')
      : null
  const effectiveConventionCartToken =
    conventionCartToken || routeConventionCartToken || null
  const shouldIncludeConventionCart =
    includeConventionCart || Boolean(effectiveConventionCartToken)
  const user = useStore(state => state.user)
  const authResolved = useStore(state => state.authResolved)
  const shoppingCartId = useStore(state => state.shoppingCartId)
  const hydrated = useStore(state => state.hydrated)
  const setCurrency = useStore(state => state.setCurrency)
  const setCountry = useStore(state => state.setCountry)
  const setCountryFreeShippingStatus = useStore(
    state => state.setCountryFreeShippingStatus
  )
  const setShoppingCartId = useStore(state => state.setShoppingCartId)
  const { setCookie, getCookie } = useCookie()
  const queryClient = useQueryClient()
  const prevUserRef = useRef<number | undefined>(user?.id)
  const prevCartIdRef = useRef<string | null>(shoppingCartId)
  const normalizedConventionCartToken = normalizeCartToken(
    effectiveConventionCartToken
  )
  const normalizedShoppingCartId = normalizeCartToken(shoppingCartId)

  const { mutate: removeItem } = useRemoveCartItemMutation({
    onSuccess: async () => {
      // First invalidate and refetch cart to get updated items
      await queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      // Wait for cart to refetch so payment methods query has latest cart data
      await queryClient.refetchQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      // Then invalidate and refetch payment methods query since payment plan availability may have changed
      await queryClient.invalidateQueries({
        queryKey: ['AvailablePaymentMethodsForShoppingCart'],
        exact: false,
      })
      // Force refetch to ensure payment methods are updated immediately
      await queryClient.refetchQueries({
        queryKey: ['AvailablePaymentMethodsForShoppingCart'],
        exact: false,
      })
    },
  })

  // Invalidate queries when user or cart changes (but don't remove them)
  useEffect(() => {
    if (!hydrated) return

    const userChanged = prevUserRef.current !== user?.id
    const cartIdChanged = prevCartIdRef.current !== shoppingCartId

    // Only invalidate if transitioning between states (not on initial mount)
    const hasTransitioned =
      prevUserRef.current !== undefined || prevCartIdRef.current !== undefined

    if (hasTransitioned && (userChanged || cartIdChanged)) {
      // Invalidate instead of remove - allows refetch to complete
      queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
      })
    }

    prevUserRef.current = user?.id
    prevCartIdRef.current = shoppingCartId
  }, [user?.id, shoppingCartId, hydrated, queryClient])

  const queryVariables = (() => {
    if (
      shouldIncludeConventionCart &&
      isGuestCartToken(normalizedConventionCartToken)
    ) {
      return {
        anonymousCartToken: normalizedConventionCartToken,
        includeConventionCart: true,
      }
    }
    if (authResolved && user?.id) {
      return {
        customerId: user.id,
        includeConventionCart: false,
      }
    }
    if (isGuestCartToken(normalizedShoppingCartId)) {
      return {
        anonymousCartToken: normalizedShoppingCartId,
        includeConventionCart: false,
      }
    }
    return undefined
  })()

  const shouldQueryCart =
    Boolean(
      queryVariables?.anonymousCartToken &&
      queryVariables.anonymousCartToken.trim().length > 0
    ) || Boolean(queryVariables?.customerId)

  const activeShoppingCartQueryKey = useMemo(
    () =>
      queryVariables
        ? useActiveShoppingCartQuery.getKey(queryVariables)
        : ['ActiveShoppingCartDisabled'],
    [queryVariables]
  )

  useEffect(() => {
    if (!hydrated || shoppingCartId == null) {
      return
    }

    if (normalizedShoppingCartId !== shoppingCartId) {
      setShoppingCartId(normalizedShoppingCartId)
      return
    }

    if (!isGuestCartToken(normalizedShoppingCartId)) {
      setShoppingCartId(null)
    }
  }, [hydrated, normalizedShoppingCartId, setShoppingCartId, shoppingCartId])

  const activeShoppingCartQuery = useQuery<ActiveShoppingCartQuery, Error>({
    queryKey: activeShoppingCartQueryKey,
    queryFn: queryVariables
      ? useActiveShoppingCartQuery.fetcher(queryVariables)
      : async (): Promise<ActiveShoppingCartQuery> => ({
          activeShoppingCart: null,
        }),
    enabled: enabled && shouldQueryCart && hydrated,
    retry: 1,
    retryDelay: 1000,
    staleTime: CART_QUERY_STALE_TIME_MS,
    gcTime: CART_QUERY_GC_TIME_MS,
  })

  useEffect(() => {
    const cartError = activeShoppingCartQuery.error
    if (
      !cartError ||
      queryVariables?.customerId ||
      !queryVariables?.anonymousCartToken ||
      !cartError.message.includes(INVALID_CART_IDENTIFIER_MESSAGE)
    ) {
      return
    }

    if (queryVariables.anonymousCartToken === normalizedShoppingCartId) {
      setShoppingCartId(null)
    }

    queryClient.removeQueries({
      queryKey: activeShoppingCartQueryKey,
      exact: true,
    })
  }, [
    activeShoppingCartQuery.error,
    activeShoppingCartQueryKey,
    normalizedShoppingCartId,
    queryClient,
    queryVariables,
    setShoppingCartId,
  ])

  const cartData = activeShoppingCartQuery.data?.activeShoppingCart
  const { isPending, isError, isLoading, error, refetch } =
    activeShoppingCartQuery

  // Sync store currency, country, and brand cookie with cart data whenever it changes
  useEffect(() => {
    if (shouldIncludeConventionCart) {
      return
    }

    if (cartData) {
      const cart = cartData
      const currentBrandCookie = getCookie(BRAND_COOKIE)

      // Determine brand implied by this cart snapshot (backend may omit brand.code)
      const isUKCart =
        cart.country?.isoCode === 'GB' ||
        cart.country?.isoCode === 'UK' ||
        cart.currency?.code?.toUpperCase() === 'GBP'
      const cartImpliedBrand = isUKCart
        ? 'VANISHING_INC_UK'
        : 'VANISHING_INC_US'
      const hostBrand =
        typeof window !== 'undefined'
          ? getBrandCodeForHost(window.location.host)
          : null
      // Host is the single source of truth for market/currency enforcement.
      const trustedBrand = hostBrand

      // UK host while cart is still US-shaped (repricing lag after moving hosts)
      if (
        trustedBrand === 'VANISHING_INC_UK' &&
        cartImpliedBrand === 'VANISHING_INC_US'
      ) {
        setCurrency(UK_STOREFRONT_CURRENCY_ID)
        return
      }

      // US host while cart is still GBP/UK-shaped (repricing lag after moving hosts)
      if (
        trustedBrand === 'VANISHING_INC_US' &&
        cartImpliedBrand === 'VANISHING_INC_UK'
      ) {
        setCurrency(US_STOREFRONT_CURRENCY_ID)
        return
      }

      const isCartAlignedWithHost =
        !trustedBrand ||
        (trustedBrand === 'VANISHING_INC_UK' &&
          cartImpliedBrand === 'VANISHING_INC_UK') ||
        (trustedBrand === 'VANISHING_INC_US' &&
          cartImpliedBrand === 'VANISHING_INC_US')

      // Update currency from cart only when cart market matches current host.
      if (isCartAlignedWithHost && cart.currency?.id) {
        setCurrency(cart.currency.id)
      }

      // Update country from cart only when cart market matches current host.
      if (isCartAlignedWithHost && cart.country?.isoCode) {
        setCountry(cart.country.isoCode)
        setCookie(COUNTRY_COOKIE, cart.country.isoCode)
        setCountryFreeShippingStatus(cart.country.freeShipping ?? null)
      }

      // Keep vi-brand aligned with current host market to prevent cookie drift.
      if (trustedBrand && currentBrandCookie !== trustedBrand) {
        setCookie(BRAND_COOKIE, trustedBrand)
      }
      return
    }

    // No cart snapshot means no verified country-level free-shipping signal.
    setCountryFreeShippingStatus(null)
  }, [
    shouldIncludeConventionCart,
    cartData,
    setCurrency,
    setCountry,
    setCountryFreeShippingStatus,
    setCookie,
    getCookie,
  ])

  return {
    cartData,
    totalQuantity:
      cartData?.items?.reduce((acc, item) => acc + item.quantity, 0) ?? 0,
    isPending,
    isLoading,
    isError,
    error,
    removeItem,
    refetch,
  }
}
